import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  RadioGroup,
  FormControlLabel,
  useTheme
} from '@mui/material';
import { getSettings, getPlans, getAttempts, calculateMasteryMetrics, saveSettings, savePlans } from '../utils/storage';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { getBookNameById, bibleBooks } from '../utils/books';
import { generateRangeReference } from '../utils/rangeUtils';
import { getChapters, getVerses } from '../services/bibleApi';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';

const PlanView = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Verse picker state for editing
  const [selectionMode, setSelectionMode] = useState('single');
  const [startRef, setStartRef] = useState(null);
  const [endRef, setEndRef] = useState(null);
  const [pickerBook, setPickerBook] = useState('GEN');
  const [pickerChapter, setPickerChapter] = useState(1);
  const [pickerVerse, setPickerVerse] = useState(1);
  const [selectedVersesForEdit, setSelectedVersesForEdit] = useState([]);
  const [availableChapters, setAvailableChapters] = useState([]);
  const [availableVerses, setAvailableVerses] = useState([]);

  const bibleId = getSettings().selectedBibleId;

  // Load plan data based on URL parameter
  useEffect(() => {
    if (planId) {
      const plans = getPlans();
      const plan = plans.find(p => p.id === planId);
      if (plan) {
        setSelectedPlan(plan);
      } else {
        setError('Plan not found');
      }
    }
  }, [planId]);

  const getVerseProgress = (verseId) => {
    const attempts = getAttempts();
    const verseAttempts = attempts.filter(attempt => attempt.verseId === verseId);
    if (verseAttempts.length === 0) return { status: 'not-started', color: 'default', icon: 'radio_button_unchecked' };
    
    const metrics = calculateMasteryMetrics(verseAttempts);
    if (metrics.isMastered) {
      return { status: 'mastered', color: 'success', icon: 'check_circle' };
    } else if (metrics.accuracy >= 70) {
      return { status: 'in-progress', color: 'primary', icon: 'radio_button_unchecked' };
    } else {
      return { status: 'struggling', color: 'warning', icon: 'radio_button_unchecked' };
    }
  };

  // Convert verse reference to full book name format
  const normalizeVerseReference = (ref) => {
    const parts = ref.trim().split(/\s+/);
    if (parts.length < 2) return ref;
    
    const firstPart = parts[0];
    if (firstPart.length <= 3 && firstPart === firstPart.toUpperCase() && firstPart !== firstPart.toLowerCase()) {
      const bookName = getBookNameById(firstPart);
      if (bookName) {
        return ref.replace(firstPart, bookName);
      }
    }
    return ref;
  };

  const handleVerseMemorize = (verse) => {
    // Convert verse reference to full format and save to settings
    const normalizedReference = normalizeVerseReference(verse);
    saveSettings({ ...getSettings(), selectedVerseReference: normalizedReference });
    navigate('/memorize');
  };

  const handleDeleteVerse = (verseToRemove) => {
    if (window.confirm('Are you sure you want to remove this verse from the plan?')) {
      const updatedVerses = selectedPlan.verses.filter(v => v !== verseToRemove);
      const updatedPlan = { ...selectedPlan, verses: updatedVerses };
      setSelectedPlan(updatedPlan);
      const plans = getPlans();
      const updatedPlans = plans.map(p => p.id === planId ? updatedPlan : p);
      savePlans(updatedPlans);
    }
  };

  // Verse picker functions for edit dialog
  const loadChaptersForEditBook = useCallback(async () => {
    try {
      // eslint-disable-next-line no-undef
      const chapters = await getChapters(bibleId, pickerBook);
      setAvailableChapters(chapters);
      if (chapters.length > 0 && (!pickerChapter || pickerChapter > chapters.length)) {
        setPickerChapter(1);
      }
    } catch (error) {
      console.warn('Error loading chapters:', error);
      setAvailableChapters(Array.from({ length: pickerBook === 'PSA' ? 150 : pickerBook === 'MAT' ? 28 : 50 }, (_, i) => ({ id: (i + 1).toString(), number: i + 1 })));
    }
  }, [pickerBook, pickerChapter, bibleId]);

  const loadVersesForEditChapter = useCallback(async () => {
    try {
      // eslint-disable-next-line no-undef
      const verses = await getVerses(bibleId, `${pickerBook}.${pickerChapter}`);
      setAvailableVerses(verses);
      if (verses.length > 0 && (!pickerVerse || pickerVerse > verses.length)) {
        setPickerVerse(1);
      }
    } catch (error) {
      console.warn('Error loading verses:', error);
      setAvailableVerses(Array.from({ length: 30 }, (_, i) => ({ id: (i + 1).toString(), number: i + 1 })));
    }
  }, [pickerBook, pickerChapter, pickerVerse, bibleId]);

  const handleOpenEditDialog = () => {
    setSelectedVersesForEdit(selectedPlan.verses || []);
    setStartRef(null);
    setEndRef(null);
    setSelectionMode('single');
    setPickerBook('GEN');
    setPickerChapter(1);
    setPickerVerse(1);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedVersesForEdit([]);
  };

  const handleSavePlanVerses = () => {
    const updatedPlan = { ...selectedPlan, verses: selectedVersesForEdit };
    setSelectedPlan(updatedPlan);
    const plans = getPlans();
    const updatedPlans = plans.map(p => p.id === planId ? updatedPlan : p);
    savePlans(updatedPlans);
    handleCloseEditDialog();
  };

  const handleEditBookSelect = (bookId) => {
    setPickerBook(bookId);
    setPickerChapter(1);
    setPickerVerse(1);
  };

  const handleEditChapterSelect = (chapterNum) => {
    setPickerChapter(chapterNum);
    setPickerVerse(1);
  };

  const handleEditVerseSelect = (verseNum) => {
    if (selectionMode === 'single') {
      setStartRef(null);
      setEndRef(null);
      setPickerVerse(verseNum);
    } else if (selectionMode === 'range') {
      if (!startRef) {
        // Set start verse
        setStartRef({ book: pickerBook, chapter: pickerChapter, verse: verseNum });
        setEndRef(null);
        setPickerVerse(verseNum);
      } else if (!endRef) {
        // Set end verse
        const end = { book: pickerBook, chapter: pickerChapter, verse: verseNum };
        const start = startRef;
        // Ensure start is before end
        const startBookIndex = bibleBooks.findIndex(b => b.id === start.book);
        const endBookIndex = bibleBooks.findIndex(b => b.id === end.book);
        if (startBookIndex < endBookIndex || (startBookIndex === endBookIndex && (start.chapter < end.chapter || (start.chapter === end.chapter && start.verse <= end.verse)))) {
          setEndRef(end);
        } else {
          // Swap
          setEndRef(start);
          setStartRef(end);
        }
        setPickerVerse(verseNum);
      } else {
        // Reset if both set
        setStartRef({ book: pickerBook, chapter: pickerChapter, verse: verseNum });
        setEndRef(null);
        setPickerVerse(verseNum);
      }
    }
  };

  const handleAddVerseToEdit = () => {
    let reference = '';

    if (selectionMode === 'single') {
      const selectedBook = bibleBooks.find(b => b.id === pickerBook);
      const bookName = selectedBook ? selectedBook.name : pickerBook;
      reference = `${bookName} ${pickerChapter}:${pickerVerse}`;
    } else if (selectionMode === 'range' && startRef && endRef) {
      reference = generateRangeReference(startRef.book, startRef.chapter, startRef.verse, endRef.book, endRef.chapter, endRef.verse);
    }

    // Check if this reference is already selected
    if (reference && !selectedVersesForEdit.includes(reference)) {
      setSelectedVersesForEdit([...selectedVersesForEdit, reference]);
      // Reset picker state after adding
      setStartRef(null);
      setEndRef(null);
      setSelectionMode('single');
      // Keep current picker position for convenience
    }
  };

  const handleRemoveVerseFromEdit = (verseToRemove) => {
    setSelectedVersesForEdit(selectedVersesForEdit.filter(v => v !== verseToRemove));
  };

  const canAddCurrentEditSelection = () => {
    let reference = '';

    if (selectionMode === 'single') {
      const selectedBook = bibleBooks.find(b => b.id === pickerBook);
      const bookName = selectedBook ? selectedBook.name : pickerBook;
      reference = `${bookName} ${pickerChapter}:${pickerVerse}`;
    } else if (selectionMode === 'range' && startRef && endRef) {
      reference = generateRangeReference(startRef.book, startRef.chapter, startRef.verse, endRef.book, endRef.chapter, endRef.verse);
    }

    return reference && !selectedVersesForEdit.includes(reference);
  };

  // useEffects for edit dialog
  useEffect(() => {
    if (editDialogOpen && bibleId) {
      loadChaptersForEditBook();
    }
  }, [editDialogOpen, pickerBook, bibleId, loadChaptersForEditBook]);

  useEffect(() => {
    if (editDialogOpen && bibleId) {
      loadVersesForEditChapter();
    }
  }, [editDialogOpen, pickerBook, pickerChapter, bibleId, loadVersesForEditChapter]);

  if (error) {
    return (
      <Box p={3} sx={{ textAlign: 'center' }}>
        <LibraryBooksIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/plans')}
          sx={{ mt: 2 }}
        >
          Back to Plans
        </Button>
      </Box>
    );
  }

  if (!selectedPlan) {
    return (
      <Box p={3} sx={{ textAlign: 'center' }}>
        <Typography variant="h6">Loading plan...</Typography>
      </Box>
    );
  }

  const planProgress = selectedPlan.verses ? selectedPlan.verses.reduce((acc, verse) => {
    const progress = getVerseProgress(verse);
    if (progress.status === 'mastered') acc.mastered++;
    else if (progress.status === 'in-progress') acc.inProgress++;
    else acc.notStarted++;
    return acc;
  }, { mastered: 0, inProgress: 0, notStarted: 0 }) : { mastered: 0, inProgress: 0, notStarted: 0 };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Card
        sx={{
          p: 4,
          mb: 4,
          borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: 'surfaceContainerHighest',
          border: `1px solid ${theme.palette.outline.variant}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LibraryBooksIcon sx={{ mr: 2, fontSize: 40, color: theme.palette.primary.main }} />
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                color: theme.palette.text.primary, 
                fontWeight: 600,
                mb: 1
              }}
            >
              {selectedPlan.name}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ color: theme.palette.text.secondary }}
            >
              {selectedPlan.description || 'No description available'}
            </Typography>
          </Box>
        </Box>

        {/* Progress Summary */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={4}>
            <Card sx={{ p: 2, textAlign: 'center', backgroundColor: theme.palette.success.main, boxShadow: theme.shadows[3], border: `1px solid ${theme.palette.success.main}` }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                {planProgress.mastered}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>Mastered</Typography>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card sx={{ p: 2, textAlign: 'center', backgroundColor: theme.palette.primary.main, boxShadow: theme.shadows[3], border: `1px solid ${theme.palette.primary.main}` }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                {planProgress.inProgress}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>In Progress</Typography>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card sx={{ p: 2, textAlign: 'center', backgroundColor: theme.palette.grey[300], border: `1px solid ${theme.palette.grey[400]}` }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                {planProgress.notStarted}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Not Started</Typography>
            </Card>
          </Grid>
        </Grid>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/plans')}
          sx={{ px: 4, py: 1.5 }}
        >
          Back to Plans
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleOpenEditDialog}
          size="large"
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: theme.shape.borderRadius * 1.5,
            background: `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: theme.shadows[2],
            '&:hover': {
              background: `linear-gradient(145deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: theme.shadows[4],
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          Edit Verses
        </Button>
      </Box>

      {/* Verse Cards */}
      {selectedPlan.verses?.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <SchoolIcon sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2, opacity: 0.6 }} />
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              mb: 1
            }}
          >
            No verses in this plan
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, opacity: 0.8 }}
          >
            Add some verses to your plan to get started
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {selectedPlan.verses.map((verse, index) => {
            const progress = getVerseProgress(verse);
            const attempts = getAttempts();
            const verseAttempts = attempts.filter(attempt => attempt.verseId === verse);
            const averageAccuracy = verseAttempts.length > 0 
              ? Math.round(verseAttempts.reduce((sum, attempt) => sum + attempt.accuracy, 0) / verseAttempts.length)
              : 0;
            const masteryScore = progress.status === 'mastered' ? 100 : averageAccuracy;

            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    borderRadius: theme.shape.borderRadius * 2,
                    backgroundColor: 'background.surfaceContainerLow',
                    border: `1px solid ${theme.palette.outline.light}`,
                    height: '100%',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'background.surfaceContainer',
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4],
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: theme.palette.error.main,
                        '&:hover': {
                          backgroundColor: theme.palette.error.main + '20',
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteVerse(verse);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pr: 5 }}>
                      <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 500, flex: 1 }}>
                        {verse}
                      </Typography>
                      <Chip
                        size="small"
                        label={progress.status.replace('-', ' ')}
                        sx={{
                          fontSize: '0.75rem',
                          height: 22,
                          backgroundColor: `${progress.color === 'success' ? theme.palette.success.main : progress.color === 'primary' ? theme.palette.primary.main : progress.color === 'warning' ? theme.palette.warning.main : theme.palette.grey[400]}20`,
                          color: progress.color === 'success' ? theme.palette.success.main : progress.color === 'primary' ? theme.palette.primary.main : progress.color === 'warning' ? theme.palette.warning.main : theme.palette.grey[700],
                          fontWeight: 600,
                          border: `1px solid ${progress.color === 'success' ? theme.palette.success.main : progress.color === 'primary' ? theme.palette.primary.main : progress.color === 'warning' ? theme.palette.warning.main : theme.palette.grey[400]}30`,
                          '& .MuiChip-label': {
                            px: 1,
                            textTransform: 'capitalize'
                          }
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      <Chip
                        label={`Accuracy: ${averageAccuracy}%`}
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          height: 22,
                          backgroundColor: `${averageAccuracy >= 70 ? theme.palette.success.main : theme.palette.primary.main}20`,
                          color: averageAccuracy >= 70 ? theme.palette.success.main : theme.palette.primary.main,
                          fontWeight: 600,
                          border: `1px solid ${averageAccuracy >= 70 ? theme.palette.success.main : theme.palette.primary.main}30`,
                          '& .MuiChip-label': {
                            px: 1,
                          }
                        }}
                      />
                      <Chip
                        label={`Mastery: ${masteryScore}%`}
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          height: 22,
                          backgroundColor: `${masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main}20`,
                          color: masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main,
                          fontWeight: 600,
                          border: `1px solid ${masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main}30`,
                          '& .MuiChip-label': {
                            px: 1,
                          }
                        }}
                      />
                      <Chip
                        label={`Attempts: ${verseAttempts.length}`}
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          height: 22,
                          backgroundColor: theme.palette.grey[400] + '20',
                          color: theme.palette.grey[700],
                          fontWeight: 600,
                          border: `1px solid ${theme.palette.grey[400]}30`,
                          '& .MuiChip-label': {
                            px: 1,
                          }
                        }}
                      />
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleVerseMemorize(verse)}
                      sx={{ borderRadius: theme.shape.borderRadius }}
                    >
                      Memorize
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Edit Verses Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.shape.borderRadius * 2,
            background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
            border: `1px solid ${theme.palette.outline.light}`,
            boxShadow: theme.shadows[6],
            height: '80vh',
            maxHeight: '700px'
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${theme.palette.outline.light}`,
            pb: 2,
            color: theme.palette.text.primary,
            fontWeight: 600
          }}
        >
          Edit Plan Verses
        </DialogTitle>
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Selected Verses Display */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Selected Verses ({selectedVersesForEdit.length})
            </Typography>
            {selectedVersesForEdit.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedVersesForEdit.map((verse, index) => (
                  <Chip
                    key={index}
                    label={verse}
                    onDelete={() => handleRemoveVerseFromEdit(verse)}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                No verses selected yet
              </Typography>
            )}
          </Box>

          {/* Selection Mode */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <RadioGroup
                row
                value={selectionMode}
                onChange={(e) => {
                  const newMode = e.target.value;
                  setSelectionMode(newMode);
                  setStartRef(null);
                  setEndRef(null);
                }}
                sx={{ justifyContent: 'center' }}
              >
                <FormControlLabel
                  value="single"
                  control={<Radio />}
                  label="Single Verse"
                />
                <FormControlLabel
                  value="range"
                  control={<Radio />}
                  label="Range"
                />
              </RadioGroup>
            </Box>
          </Box>

          {/* Range Mode Instructions */}
          {selectionMode === 'range' && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              {startRef && endRef ? (
                <Box>
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Chip
                      label={`Range selected: ${generateRangeReference(startRef.book, startRef.chapter, startRef.verse, endRef.book, endRef.chapter, endRef.verse)}`}
                      variant="outlined"
                      color="primary"
                      sx={{
                        fontWeight: 500,
                        width: 'fit-content',
                        mx: 'auto'
                      }}
                    />
                  </Box>
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <IconButton
                      onClick={handleAddVerseToEdit}
                      disabled={!canAddCurrentEditSelection()}
                      sx={{
                        color: canAddCurrentEditSelection()
                          ? theme.palette.primary.contrastText
                          : theme.palette.action.disabled,
                        backgroundColor: canAddCurrentEditSelection()
                          ? theme.palette.primary.main
                          : theme.palette.action.disabledBackground,
                        '&:hover': {
                          backgroundColor: canAddCurrentEditSelection()
                            ? theme.palette.primary.dark
                            : 'transparent',
                          color: canAddCurrentEditSelection()
                            ? theme.palette.primary.contrastText
                            : theme.palette.action.disabled,
                        },
                        '&.Mui-disabled': {
                          backgroundColor: theme.palette.action.disabledBackground,
                          color: theme.palette.action.disabled,
                        }
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                </Box>
              ) : startRef ? (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Start: {bibleBooks.find(b => b.id === startRef.book)?.name || startRef.book} {startRef.chapter}:{startRef.verse} - Select end verse
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Select start verse
                </Typography>
              )}
            </Box>
          )}

          {/* Single Verse Selection Display */}
          {selectionMode === 'single' && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Chip
                  label={`Verse selected: ${bibleBooks.find(b => b.id === pickerBook)?.name || pickerBook} ${pickerChapter}:${pickerVerse}`}
                  variant="outlined"
                  color="primary"
                  sx={{
                    fontWeight: 500
                  }}
                />
              </Box>
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <IconButton
                  onClick={handleAddVerseToEdit}
                  disabled={!canAddCurrentEditSelection()}
                  sx={{
                    color: canAddCurrentEditSelection()
                      ? theme.palette.primary.contrastText
                      : theme.palette.action.disabled,
                    backgroundColor: canAddCurrentEditSelection()
                      ? theme.palette.primary.main
                      : theme.palette.action.disabledBackground,
                    '&:hover': {
                      backgroundColor: canAddCurrentEditSelection()
                        ? theme.palette.primary.dark
                        : 'transparent',
                      color: canAddCurrentEditSelection()
                        ? theme.palette.primary.contrastText
                        : theme.palette.action.disabled,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Verse Picker */}
          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', gap: 1, borderRadius: '10px', border: `1px solid ${theme.palette.outline.light}` }}>
            {/* Books Column */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${theme.palette.outline.light}` }}>
              <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 600, textAlign: 'center', bgcolor: theme.palette.background.paper }}>
                Book
              </Typography>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {bibleBooks.map((book) => (
                  <ListItem key={book.id} disablePadding>
                    <ListItemButton
                      selected={book.id === pickerBook}
                      onClick={() => handleEditBookSelect(book.id)}
                      sx={{
                        py: 1,
                        px: 2,
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        },
                      }}
                    >
                      <ListItemText primary={book.name} sx={{ textAlign: 'center', m: 0 }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Box>
            </Box>

            {/* Chapters Column */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${theme.palette.outline.light}` }}>
              <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 600, textAlign: 'center', bgcolor: theme.palette.background.paper }}>
                Chapter
              </Typography>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {availableChapters.map((chapter) => (
                  <ListItem key={chapter.id} disablePadding>
                    <ListItemButton
                      selected={chapter.number === pickerChapter}
                      onClick={() => handleEditChapterSelect(chapter.number)}
                      sx={{
                        py: 1,
                        px: 2,
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        },
                      }}
                    >
                      <ListItemText primary={chapter.number} sx={{ textAlign: 'center', m: 0 }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Box>
            </Box>

            {/* Verses Column */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 600, textAlign: 'center', bgcolor: theme.palette.background.paper }}>
                Verse
              </Typography>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {availableVerses.map((verse) => (
                  <ListItem key={verse.id} disablePadding>
                    <ListItemButton
                      selected={verse.number === pickerVerse}
                      onClick={() => handleEditVerseSelect(verse.number)}
                      sx={{
                        py: 1,
                        px: 2,
                        backgroundColor: selectionMode === 'range' && startRef && endRef && verse.number >= startRef.verse && verse.number <= endRef.verse && endRef.chapter === pickerChapter && endRef.book === pickerBook && startRef.chapter === pickerChapter && startRef.book === pickerBook
                          ? `${theme.palette.secondary.main}30`
                          : verse.number === pickerVerse
                            ? theme.palette.primary.main
                            : 'transparent',
                        color: verse.number === pickerVerse
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary,
                        '&:hover': {
                          backgroundColor: verse.number === pickerVerse
                            ? theme.palette.primary.dark
                            : selectionMode === 'range' && startRef && endRef && verse.number >= startRef.verse && verse.number <= endRef.verse && endRef.chapter === pickerChapter && endRef.book === pickerBook && startRef.chapter === pickerChapter && startRef.book === pickerBook
                              ? `${theme.palette.secondary.main}50`
                              : `${theme.palette.primary.main}10`,
                        },
                      }}
                    >
                      <ListItemText primary={verse.number} sx={{ textAlign: 'center', m: 0 }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.outline.light}`,
            gap: 1
          }}
        >
          <Button
            onClick={handleCloseEditDialog}
            sx={{
              px: 3,
              py: 1,
              borderRadius: theme.shape.borderRadius,
              textTransform: 'none',
              fontWeight: 600,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePlanVerses}
            variant="contained"
            sx={{
              px: 4,
              py: 1,
              borderRadius: theme.shape.borderRadius * 1.5,
              textTransform: 'none',
              fontWeight: 600,
              background: `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: theme.shadows[2],
              '&:hover': {
                background: `linear-gradient(145deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: theme.shadows[4],
                transform: 'translateY(-1px)'
              },
              '&:disabled': {
                backgroundColor: theme.palette.action.disabled,
                boxShadow: 'none',
                transform: 'none'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanView;
